import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory } from 'react-router-dom';
import { useWidgetProps } from '../WidgetPropsContext';
import { useGlobalBackButton } from '../GlobalBackButtonContext';
import { GroupInput } from '../components/GroupInput/GroupInput';
import { FormGroup } from '../components/FormGroup/FormGroup';
import { FormTitle } from '../components/FormTitle/FormTitle';
import { Button } from '../components/Button/Button';
import { Warning } from '../components/Warning/Warning';
import { RECAPTCHA_KEY } from '../config';
import { retractConsent } from '../api';
import { mapRevokeFormToApiBody } from './mapRevokeFormToApiBody';
import { ConsentSuccess } from './ConsentSuccess';
import { getErrorMessage } from '../utils';
import { COOKIE_POLICY_URL } from '../utils/constants';
import { providerConsents } from '../providerConsents';
import './documents.scss';

const documents = require('b_').with('documents');

const text = require('b_').with('text');

const form = require('b_').with('form');

const RECAPTCHA_ERROR_CLASS = 'input__group_recaptcha_error';
const RECAPTCHA_CLASS = 'input__group input__group_recaptcha';
const YOUR_DATA = 'Ваши данные';
const SEX = 'Пол';

export const revokeConsentFormFields = {
    lastName: {
        id: 'lastName',
        type: 'text',
        required: true,
        label: 'Фамилия',
        validationType: 'fio',
        value: '',
        error: null,
    },
    firstName: {
        id: 'firstName',
        type: 'text',
        required: true,
        label: 'Имя',
        validationType: 'fio',
        value: '',
        error: null,
    },
    middleName: {
        id: 'middleName',
        type: 'text',
        required: true,
        label: 'Отчество',
        validationType: 'fioOrDash',
        value: '',
        error: null,
    },
    birthDate: {
        id: 'birthDate',
        type: 'date',
        required: true,
        label: 'Дата рождения',
        validationType: 'date_less',
        value: '',
        error: null,
    },
    sex: {
        id: 'sex',
        type: 'radio',
        required: true,
        metaData: [
            { id: 'female', name: 'Жен' },
            { id: 'male', name: 'Муж' },
        ],
        title: 'Пол',
        validationType: 'radio',
        value: '',
        error: null,
    },
    phone: {
        id: 'phone',
        type: 'phone',
        required: true,
        validationType: 'phone',
        label: 'Телефон',
        value: '',
        error: null,
    },
};

// eslint-disable-next-line max-statements
export const Documents = () => {
    const { providerCode } = useWidgetProps();

    const history = useHistory();
    const { setOnBack } = useGlobalBackButton();

    const currentConsents = providerConsents[providerCode];
    const onlineBookingLink = currentConsents?.onlineBookingLink;
    const onlineBookingChildLink = currentConsents?.onlineBookingChildLink;

    const [ formData, setFormData ] = useState({
        lastName: { value: '', error: null },
        firstName: { value: '', error: null },
        middleName: { value: '', error: null },
        birthDate: { value: '', error: null },
        sex: { value: '', error: null },
        phone: { value: '', error: null },
    });

    const recaptchaRef = useRef();
    const recaptchaContainerRef = useRef();
    const [ isRecaptchaVerified, setIsRecaptchaVerified ] = useState(false);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ submitError, setSubmitError ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);

    useEffect(() => {
        const handleBack = () => {
            history.push('/');
        };

        setOnBack(() => handleBack);

        return () => {
            setOnBack(undefined);
        };
    }, [ history, setOnBack ]);

    const handleInputChange = (fieldId, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldId]: {
                value,
                error: null,
            },
        }));
    };

    const validateField = (fieldId, value) => {
        const fieldConfig = revokeConsentFormFields[fieldId];
        const error = getErrorMessage({
            ...fieldConfig,
            value,
        });

        setFormData(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                error,
            },
        }));
    };

    const onRecaptcha = (value) => {
        setIsRecaptchaVerified(!!value);
        if (recaptchaContainerRef.current) {
            recaptchaContainerRef.current.classList.toggle(RECAPTCHA_ERROR_CLASS, !value);
        }
    };

    const isFormValid = () => {
        const allFieldsValid = Object.keys(formData).every((fieldId) => {
            const fieldConfig = revokeConsentFormFields[fieldId];
            const fieldValue = formData[fieldId].value;
            const error = getErrorMessage({
                ...fieldConfig,
                value: fieldValue,
            });
            return !error;
        });

        return allFieldsValid && isRecaptchaVerified;
    };

    const handleSubmit = async () => {
        setSubmitError(false);
        const recaptchaValue = recaptchaRef.current?.getValue();
        if (!recaptchaValue) {
            if (recaptchaContainerRef.current) {
                recaptchaContainerRef.current.classList.add(RECAPTCHA_ERROR_CLASS);
            }
            return;
        }
        setIsSubmitting(true);
        try {
            const body = mapRevokeFormToApiBody(formData, recaptchaValue);
            await retractConsent(providerCode, body);
            setIsSuccess(true);
        } catch (err) {
            setSubmitError(true);
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
                setIsRecaptchaVerified(false);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setFormData({
            lastName: { value: '', error: null },
            firstName: { value: '', error: null },
            middleName: { value: '', error: null },
            birthDate: { value: '', error: null },
            sex: { value: '', error: null },
            phone: { value: '', error: null },
        });
        setIsRecaptchaVerified(false);
        setSubmitError(false);
        setIsSuccess(false);
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    };

    return (
        <div className={documents()}>
            <div className={documents('container')}>
                {isSuccess ? (
                    <ConsentSuccess
                        name={`${formData.lastName.value} ${formData.firstName.value} ${formData.middleName.value}`}
                        onReset={handleReset}
                    />
                ) : (
                    <>
                        <div className={documents('links-section')}>
                            <a href={COOKIE_POLICY_URL} className={documents('document-link')} rel="noreferrer" target="_blank">
                                Обработка файлов cookie
                            </a>

                            {onlineBookingLink && (
                                <a
                                    href={onlineBookingLink}
                                    className={documents('document-link')}
                                    rel="noreferrer"
                                    target="_blank">
                                    Согласие на обработку моих персональных данных для онлайн-записи на оказание медицинской
                                    помощи с
                                    внесением сведений в медицинские информационные системы (ресурсы)
                                </a>
                            )}

                            {onlineBookingChildLink && (
                                <a
                                    href={onlineBookingChildLink}
                                    className={documents('document-link')}
                                    rel="noreferrer"
                                    target="_blank">
                                    Согласие на обработку персональных данных моего ребенка для онлайн-записи на оказание
                                    медицинской помощи
                                    с внесением сведений в медицинские информационные системы (ресурсы)
                                </a>
                            )}
                        </div>

                        <div className={documents('revoke-section')}>
                            <p className={text()}>Для того, чтобы отозвать ранее данное согласие заполните форму
                                ниже:</p>

                            <form className={form()} style={{ paddingTop: '32px' }}>
                                <FormTitle title={YOUR_DATA}/>
                                <GroupInput
                                    {...revokeConsentFormFields.lastName}
                                    value={formData.lastName.value}
                                    error={formData.lastName.error}
                                    onChange={value => handleInputChange('lastName', value)}
                                    onBlur={value => validateField('lastName', value)}
                                />
                                <GroupInput
                                    {...revokeConsentFormFields.firstName}
                                    value={formData.firstName.value}
                                    error={formData.firstName.error}
                                    onChange={value => handleInputChange('firstName', value)}
                                    onBlur={value => validateField('firstName', value)}
                                />
                                <GroupInput
                                    {...revokeConsentFormFields.middleName}
                                    value={formData.middleName.value}
                                    error={formData.middleName.error}
                                    onChange={value => handleInputChange('middleName', value)}
                                    onBlur={value => validateField('middleName', value)}
                                />
                                <div className={form('box')}>
                                    <GroupInput
                                        {...revokeConsentFormFields.birthDate}
                                        value={formData.birthDate.value}
                                        error={formData.birthDate.error}
                                        onChange={value => handleInputChange('birthDate', value)}
                                        onBlur={value => validateField('birthDate', value)}
                                    />

                                    <FormGroup title={SEX} required>
                                        <GroupInput
                                            {...revokeConsentFormFields.sex}
                                            value={formData.sex.value}
                                            error={formData.sex.error}
                                            onChange={value => handleInputChange('sex', value)}
                                            onBlur={value => validateField('sex', value)}
                                            wrapperMod={{ sex: true }}
                                        />
                                    </FormGroup>
                                </div>
                                <GroupInput
                                    {...revokeConsentFormFields.phone}
                                    value={formData.phone.value}
                                    error={formData.phone.error}
                                    onChange={value => handleInputChange('phone', value)}
                                    onBlur={value => validateField('phone', value)}
                                />
                                {!RECAPTCHA_KEY ? null : (
                                    <div ref={recaptchaContainerRef} className={RECAPTCHA_CLASS}>
                                        <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_KEY} onChange={onRecaptcha}/>
                                    </div>
                                )}
                                {submitError && (
                                    <div style={{ paddingTop: 20 }}>
                                        <Warning text={'Ошибка, неверные данные.'} show/>
                                    </div>
                                )}
                                <div style={{ marginTop: '24px' }}>
                                    <Button onClick={handleSubmit} disabled={!isFormValid() || isSubmitting}>
                                        Отозвать
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
